import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

import _isEmpty from 'underscore/cjs/isEmpty.js'

import { useUtmsStore } from './utms'

import {
  PAGE_DATA
} from '~/constants/funnel/car-insurance'

dayjs.extend(customParseFormat)

const separateAddress = address => {
  const parts = address.split(' ')
  let streetNumber = ''
  let streetName = ''
  let buildingFloorOrUnit = ''

  if (parts.length > 0 && parts[0] === '') {
    parts.shift()
  }

  for (let i = 0; i < parts.length; i++) {
    if (isNaN(parts[i])) {
      streetName += parts[i] + ' '
    } else if (i === parts.length - 2) {
      buildingFloorOrUnit = parts[i]
    } else {
      streetNumber += parts[i] + ' '
    }
  }

  streetNumber = streetNumber.trim()
  streetName = streetName.trim()
  buildingFloorOrUnit = buildingFloorOrUnit.trim()

  // add location to buildingFloorOrUnit, if present
  if (streetName.startsWith('Ground Floor') || streetName.startsWith('Upper Ground Floor')) {
    buildingFloorOrUnit = `${streetName} ${buildingFloorOrUnit}`
    streetName = ''
  }

  return {
    streetNumber,
    streetName,
    buildingFloorOrUnit
  }
}

export const useCarInsuranceStore = defineStore('car-insurance', {
  state: () => ({
    guides: {
      url: null,
      initialPage: ''
    },
    form: {},

    yearList: [],
    makeList: [],
    modelList: [],
    variantList: [],
    seriesList: [],
    nvicOptions: [],

    driverAge: {},

    selectedCar: {},
    carDetails: [],
    nvicResult: [],

    lead: {},
    quotes: {},
    rankedPolicies: {},
    selectedPolicy: {},

    selectedPartner: {},
    partnerOffersData: {}
  }),

  getters: {
    leadParams () {
      const form = this.form

      form.registration = {
        ...form.registration, 
        ...form.vehicle
      }

      if (_isEmpty(form)) {
        return {}
      }

      const utmsStore = useUtmsStore()
      const formData = flattenObj(form, 1)

      const {
        street,
        streetSplit
      } = (() => {
        const split = formData.residential_address?.addressline
          ? formData.residential_address.addressline.split(',')
          : []

        return {
          street: split.length > 1
            ? split[1]
            : formData.residential_address?.addressline,
          streetSplit: split
        }
      })()

      const streetNum = street?.length > 1
        ? streetSplit[1]
        : streetSplit[0]

      const splitAddress = streetNum
        ? separateAddress(streetNum)
        : ''

      const address = formData?.residential_address
        ? Object.keys(formData.residential_address)
          .map(item => formData.residential_address[item])
          .join(', ')
        : null

      const name = getName(formData?.full_name)
      const vehicleColor = typeof this.selectedCar?.colour === 'object' ? this.selectedCar?.colour?.primary : this.selectedCar?.colour

      const obj = {
        name,
        full_name: name?.name,
        first_name: name?.first_name,
        last_name: name?.last_name,
        email: formData?.email,
        phone: formData?.phone,

        main_driver_employment: formData?.main_employment_status,
        main_driver_license: formData?.main_license_type,
        main_driver_other_cars: formData?.another_car
          ? formData?.another_car.toLowerCase()
          : formData?.another_car,
        main_drivers_age: formData?.main_age,
        main_drivers_birthdate: formData?.main_date_of_birth,
        main_drivers_gender: formData?.main_gender,

        younger_driver: formData?.another_driver
          ? formData?.another_driver.toLowerCase()
          : formData?.another_driver,
        younger_driver_age: formData?.second_age,
        younger_driver_birthdate: formData?.second_date_of_birth,
        younger_driver_gender: formData?.second_gender,

        cover_level: formData?.type_of_cover,
        state: formData?.residential_address?.state,
        address_suburb: formData?.residential_address?.suburb,
        address_postCode: formData?.residential_address?.postcode,
        address_street: street,
        address_street_number: splitAddress?.streetNumber,
        address_street_name: splitAddress?.streetName,

        interest_reason: formData?.looking_for,
        reg_no: formData?.rego || 'none',
        reg_state: formData?.state,
        rego_type: formData?.rego_type || 'none',

        vehicle_make: this.selectedCar?.ManufacturerName,
        vehicle_model: formData?.vehicle_model || this.selectedCar.FamilyName,
        vehicle_year: formData?.vehicle_year || this.selectedCar?.YearCreate,
        vehicle_series: this.selectedCar?.NVIC_CUR,
        vehicle_variant: formData?.vehicle_variant || this.selectedCar?.VariantName,
        vehicle_detail: {
          ...this.selectedCar,
          colour: vehicleColor || formData?.vehicle_colour,
          VehicleColor: vehicleColor || formData?.vehicle_colour,
          release: dayjs(this.selectedCar.ReleaseDate, 'YYYY-MM-DD').format('MMM'),
          vin: this.nvicResult?.[0]?.vin,
          BodyStyle: this.nvicResult?.[0]?.['body-style']
        },

        factory_option: formData?.factory_options
          ? formData?.factory_options.toLowerCase()
          : formData?.factory_options,
        factory_options: (() => {
          const options = formData?.factory_fitted_dealer_options

          return _isEmpty(options)
            ? 'none'
            : options.map(item => this.nvicOptions.find(option => option.OptionCode === item))?.OptionName
        })(),
        non_standard_accessorie: formData?.accessories
          ? formData?.accessories.toLowerCase()
          : formData?.accessories,
        non_standard_accessories: _isEmpty(formData?.non_standard_accessories)
          ? 'none'
          : formData?.non_standard_accessories,
        address,
        address_locality: formData?.residential_address?.suburb,

        vehicle_modified: formData?.modified
          ? formData?.modified.toLowerCase()
          : formData?.modified,
        vehicle_damages: formData?.repairs,
        vehicle_finance: formData?.current_finance,
        vehicle_usage: formData?.car_use,
        vehicle_mileage_usage: formData?.kms,
        vehicle_mileage_usage_frequency: '',
        vehicle_parked_at_night_type: formData?.car_park,
        vehicle_owner_type: formData?.car_use,
        vehicle_nvic_code: this.selectedCar?.NVIC_CUR,
        vehicle_purchased: formData?.vehicle_purchased,

        current_insurance: _isEmpty(formData?.current_provider)
          ? 'NOT INSURED'
          : formData?.current_provider,
        current_residential_status: formData?.residential_status,
        time_living: formData?.time_living,
        name_of_financer: _isEmpty(formData?.financer)
          ? 'none'
          : formData?.financer,
        need_certificate: _isEmpty(formData?.certificate)
          ? 'none'
          : formData?.certificate,

        policy_start: formData?.policy_start,
        restrict_age: formData?.increase_age_restriction,
        claims: formData?.how_many_claims,
        current_no_claim_discount: formData?.no_claim_discount,
        current_policy_expired: formData?.current_policy_expired,
        years_held: (() => {
          const hadInsurance = formData?.had_insurance

          if (!['Yes', 'No'].includes(hadInsurance)) {
            return hadInsurance
          }

          return (hadInsurance === 'Yes' && '1') || '0'
        })(),

        the_car_be_used_for: formData?.car_use_future || 'none',
        redbook_code: this.nvicResult?.[0]?.['redbook-code'],

        form_page: import.meta.client && window.location.href,
        landing_page: utmsStore.GET_UTMS_LANDING_PAGE
      }

      return obj
    }
  },

  actions: {
    async createLead (payload) {
      const utmsStore = useUtmsStore()

      const response = await useFetch('/api/lead/create', {
        method: 'post',
        body: {
          vertical: 'Car Insurance',
          ...utmsStore.utms,
          ...payload
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data

        return data?.lead_uuid || undefined
      }).catch(error => {
        throw error
      })

      return response
    },

    async updateLead (payload) {
      const utmsStore = useUtmsStore()

      const response = await useFetch('/api/lead/update', {
        method: 'post',
        body: {
          vertical: 'Car Insurance',
          ...utmsStore.utms,
          ...payload
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data

        return data?.lead_uuid || undefined
      }).catch(error => {
        throw error
      })

      return response
    },

    handleFormFormat (lead) {
      const details = lead?.deatails

      this.selectedCar = details?.vehicle_detail

      const registration = {
        looking_for: details?.interest_reason,
        type_of_cover: details?.cover_level
      }

      const vehicle = {
        rego: details?.reg_no === 'none' ? '' : details?.reg_no,
        rego_type: details?.rego_type === 'none' ? '' : details?.rego_type,
        vehicle_purchased: details?.vehicle_purchased,
        state: details?.reg_state
      }

      const search = (() => {
        const colour = details?.vehicle_detail?.colour?.primary || details?.vehicle_detail?.colour

        return {
          vehicle_year: details?.vehicle_year,
          vehicle_make: details?.vehicle_detail?.ManufacturerCode,
          vehicle_model: details?.vehicle_model,
          vehicle_variant: details?.vehicle_variant,
          vehicle_series: details?.vehicle_detail?.SeriesName,
          vehicle_colour: colour,
          vehicle_transmission: details?.vehicle_detail?.VehicleTransmission
        }
      })()

      const carDetails = {
        factory_options: PAGE_DATA.yesno.find(item => item.toLowerCase() === details?.factory_option),
        accessories: PAGE_DATA.yesno.find(item => item.toLowerCase() === details?.non_standard_accessorie),
        modified: PAGE_DATA.yesno.find(item => item.toLowerCase() === details?.vehicle_modified)
      }

      const options = {
        factory_fitted_dealer_options: details?.factory_options === 'none'
          ? []
          : details?.factory_options
      }

      const accessories = {
        non_standard_accessories: details?.non_standard_accessories === 'none'
          ? []
          : details?.non_standard_accessories
      }

      const about = {
        repairs: details?.vehicle_damages,
        current_finance: details?.vehicle_finance,
        certificate: details?.need_certificate,
        car_use: details?.vehicle_usage,
        car_use_future: details?.the_car_be_used_for === 'none'
          ? []
          : details?.the_car_be_used_for,
        kms: details?.vehicle_mileage_usage,
        residential_address: {
          addressline: details?.address_street,
          streetnumber: details?.address_street_number,
          streetname: details?.address_street_name,
          suburb: details?.address_suburb,
          postcode: details?.address_postCode,
          state: details?.state || (details?.address && details?.address.split(', ').slice(-1)[0]) || ''
        },
        car_park: details?.vehicle_parked_at_night_type,
        residential_status: details?.current_residential_status,
        time_living: details?.time_living
      }

      const drivers = (() => {
        const anotherDriver = (details?.younger_driver && details?.younger_driver.charAt(0).toUpperCase() + details?.younger_driver.slice(1)) || ''
        const anotherCar = (details?.main_driver_other_cars && details?.main_driver_other_cars.charAt(0).toUpperCase() + details?.main_driver_other_cars.slice(1)) || ''

        return {
          main_date_of_birth: details?.main_drivers_birthdate,
          main_gender: details?.main_drivers_gender,
          main_license_type: details?.main_driver_license,
          main_age: details?.main_drivers_age,
          main_employment_status: details?.main_driver_employment,
          another_car: anotherCar,
          another_driver: anotherDriver,
          second_date_of_birth: details?.younger_driver_birthdate,
          second_gender: details?.younger_driver_gender,
          second_age: details?.younger_driver_age
        }
      })()

      const coverage = {
        had_insurance: details?.years_held === '0'
          ? 'No'
          : 'Yes',
        current_time_insurance: details?.years_held ?? '',
        how_many_claims: details?.claims,
        no_claim_discount: details?.current_no_claim_discount
      }

      const coverageDetails = {
        current_provider: details?.current_insurance === 'NOT INSURED'
          ? ''
          : details?.current_insurance,
        current_policy_expired: details?.current_policy_expired,
        increase_age_restriction: details?.restrict_age,
        policy_start: details?.policy_start
      }

      const user = {
        full_name: details?.full_name || `${lead?.first_name} ${lead?.last_name}`,
        phone: lead?.phone,
        email: lead?.email
      }

      const payload = {
        registration,
        vehicle,
        search,
        'car-details': carDetails,
        options,
        accessories,
        about,
        drivers,
        coverage,
        'coverage-details': coverageDetails,
        user
      }

      this.form = payload
    },

    async getLead (payload) {
      const response = await useFetch('/api/lead/get', {
        method: 'get',
        params: {
          vertical: 'Car Insurance',
          ...payload
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data

        return data
      }).catch(error => {
        throw error
      })

      this.lead = response

      this.handleFormFormat(response)

      return response
    },

    async getQuotes (payload) {
      const quotes = await useFetch('/api/car-insurance/get-quotes', {
        method: 'post',
        body: {
          vertical: 'Car Insurance',
          ...payload
        }
      }).then(res => {
        if (res?.error.value) {
          this.quotes = {}
        }

        const data = res?.data.value

        this.quotes = data?.error
          ? {}
          : res?.data.value?.quotes

        return res?.data.value
      })

      return quotes
    },

    async searchByRego (body) {
      const list = await useFetch('/api/car-insurance/search-by-rego', {
        method: 'post',
        body
      }).then(res => res?.data.value || [])

      this.carDetails = []

      if (list === 'No result') {
        this.nvicResult = []

        return
      }

      this.nvicResult = !(list instanceof Array)
        ? [list]
        : list
    },

    async getCarInfo (payload) {
      const list = await useFetch('/api/car-insurance/get-car-info', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A',
          ...payload
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      this.carDetails = []

      if (list === 'No result') {
        this.nvicResult = []

        return
      }

      this.nvicResult = !(list instanceof Array)
        ? [list]
        : list

      return list || []
    },

    async searchNvic (nvic) {
      const getNvicInfo = nvicData => {
        // No delay variable, use async/await for sequential processing
        return $fetch('/api/car-insurance/get-nvic-info', {
          method: 'post',
          body: JSON.parse(JSON.stringify({
            ModelTypeCode: 'A',
            NVIC: nvicData
          }))
        })
      }

      const promises = nvic.map(getNvicInfo)
      const list = await Promise.all(promises)

      this.carDetails = list
    },

    async getYearList () {
      if (!_isEmpty(this.yearList)) {
        return
      }

      const list = await useFetch('/api/car-insurance/year-list', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A'
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      this.yearList = list

      return list || []
    },

    async getCarMakes (payload) {
      const list = await useFetch('/api/car-insurance/car-makes', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A',
          ...payload
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      this.makeList = list

      return list || []
    },

    async getCarModels (payload) {
      const list = await useFetch('/api/car-insurance/car-models', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A',
          ...payload
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      this.modelList = list

      return list || []
    },

    async getCarVariants (payload) {
      const list = await useFetch('/api/car-insurance/car-variants', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A',
          ...payload
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      this.variantList = list

      return list || []
    },

    async getCarSeries (payload) {
      const list = await useFetch('/api/car-insurance/car-series', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A',
          ...payload
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      this.seriesList = list

      return list || []
    },

    async getNvicOptions (payload) {
      const list = await useFetch('/api/car-insurance/nvic-options', {
        method: 'POST',
        body: {
          ModelTypeCode: 'A',
          ...payload
        }
      })
        .then(res => res?.data?.value || [])
        .catch(error => {
          throw error
        })

      if (list === 'No result') {
        this.nvicOptions = []

        return
      }

      this.nvicOptions = !(list instanceof Array)
        ? [list]
        : list

      return list || []
    },

    async capturePolicies (payload) {
      await useFetch('/api/car-insurance/policies', {
        method: 'POST',
        body: {
          vertical: 'Car Insurance',
          ...payload
        }
      })

      this.rankedPolicies = payload

      return payload
    },

    async handleSelectPolicy (payload) {
      await useFetch('/api/car-insurance/apply-policy', {
        method: 'POST',
        body: {
          vertical: 'Car Insurance',
          ...payload
        }
      })

      this.selectedPolicy = payload

      return payload
    },

    setSelectedPartner (partner) {
      this.selectedPartner = { ...partner }
    },
    resetSelectedPartner () {
      this.selectedPartner = {}
    },
    setPartnerOffersData (payload) {
      this.partnerOffersData = payload
    }
  },

  persist: {
    storage: piniaPluginPersistedstate.sessionStorage(),
    pick: ['selectedPartner', 'partnerOffersData']
  }
})
